/**
 * iOS-Specific Subscription Management Component
 * 
 * PHASE 3.1: iOS-Specific Features (Platform Isolated)
 * 
 * This component provides iOS-specific subscription management features
 * that are only available on iOS native apps:
 * 
 * - Deep links to App Store subscription management
 * - App Store review guidelines compliance
 * - iOS family sharing considerations
 * - Platform isolation to prevent issues on other platforms
 * 
 * Features:
 * - Manage Subscription deep link to App Store settings
 * - Family sharing information and management
 * - App Store review guidelines compliance messaging
 * - Platform-specific subscription terms and conditions
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  ExternalLink, 
  Users, 
  Shield, 
  Info,
  Settings,
  Star
} from 'lucide-react';
import { isIOSNativeApp } from '@/utils/platformUtils';
import { toast } from '@/hooks/use-toast';

interface IOSSubscriptionManagementProps {
  isVisible?: boolean;
  className?: string;
}

/**
 * iOS-Specific Subscription Management Component
 * Only renders on iOS native apps with platform isolation
 */
export function IOSSubscriptionManagement({ 
  isVisible = true, 
  className = '' 
}: IOSSubscriptionManagementProps) {
  const [isOpeningSettings, setIsOpeningSettings] = useState(false);
  
  // Platform isolation - only render on iOS native apps
  const isIOSNative = isIOSNativeApp();
  
  if (!isIOSNative || !isVisible) {
    return null;
  }

  /**
   * Open iOS App Store subscription management
   * Deep link to user's subscription settings in the App Store
   */
  const handleManageSubscription = async () => {
    setIsOpeningSettings(true);
    
    try {
      // iOS deep link to App Store subscription management
      // This opens the user's subscription management page in the App Store
      const appStoreURL = 'https://apps.apple.com/account/subscriptions';
      
      console.log('📱 iOS Subscription Management: Opening App Store subscription settings');
      
      // Use window.open for iOS native apps (Capacitor handles this properly)
      window.open(appStoreURL, '_system');
      
      toast({
        title: 'Opening App Store',
        description: 'Redirecting to your subscription management page...',
      });
      
    } catch (error) {
      console.error('❌ iOS Subscription Management: Failed to open App Store settings:', error);
      toast({
        title: 'Unable to Open Settings',
        description: 'Please manually open the App Store and go to your account settings.',
        variant: 'destructive'
      });
    } finally {
      setIsOpeningSettings(false);
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* iOS Subscription Management Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            <CardTitle className="text-lg">Manage Your Subscription</CardTitle>
            <Badge variant="outline" className="text-xs">
              🍎 iOS
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Manage your subscription, view billing history, and update payment methods 
            through your App Store account settings.
          </p>
          
          <Button 
            onClick={handleManageSubscription}
            disabled={isOpeningSettings}
            className="w-full"
            variant="outline"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            {isOpeningSettings ? 'Opening App Store...' : 'Manage in App Store'}
          </Button>
        </CardContent>
      </Card>

      {/* iOS Family Sharing Information */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            <CardTitle className="text-lg">Family Sharing</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Your subscription can be shared with up to 5 family members through 
            iOS Family Sharing when enabled.
          </p>
          
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription className="text-sm">
              To enable Family Sharing for this subscription, go to your App Store 
              account settings and set up Family Sharing. All family members will 
              have access to premium features.
            </AlertDescription>
          </Alert>
          
          <div className="text-xs text-muted-foreground">
            <p>• Family organizer manages the subscription</p>
            <p>• All family members get premium access</p>
            <p>• Billing is handled through the organizer's account</p>
          </div>
        </CardContent>
      </Card>

      {/* App Store Review Guidelines Compliance */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            <CardTitle className="text-lg">Subscription Terms</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm space-y-2">
            <p className="font-medium">Auto-Renewal Information:</p>
            <div className="text-muted-foreground space-y-1">
              <p>• Payment will be charged to your Apple ID account at confirmation of purchase</p>
              <p>• Subscription automatically renews unless auto-renew is turned off at least 24 hours before the end of the current period</p>
              <p>• Your account will be charged for renewal within 24 hours prior to the end of the current period</p>
              <p>• You can manage and cancel your subscriptions by going to your App Store account settings after purchase</p>
            </div>
          </div>
          
          <Alert>
            <Star className="h-4 w-4" />
            <AlertDescription className="text-sm">
              <strong>Free Trial:</strong> If offered, any unused portion of a free trial period 
              will be forfeited when you purchase a subscription.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Privacy and Terms Links */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Legal Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col space-y-2">
            <Button
              variant="ghost"
              className="justify-start h-auto p-2"
              onClick={() => window.open('https://thematmonster.com/legal#privacy', '_system')}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Privacy Policy
            </Button>
            <Button
              variant="ghost"
              className="justify-start h-auto p-2"
              onClick={() => window.open('https://www.apple.com/legal/internet-services/itunes/dev/stdeula/', '_system')}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Terms of Service
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default IOSSubscriptionManagement;
